import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useStore } from 'react-redux';
import { YesOrNo } from '@display-component/shared';
import {
  NewTextButton,
  useAlert,
  useConfirm,
  useToast,
} from '@design-system/pc';
import { authGateway } from '@/di-container/auth/gateway/client';
import { scrapGateway } from '@/di-container/common/scrap/gateway/client';
import { ScrapType } from '@remodeling/common';
import useAuthenticated from '@/hooks/useAuthenticated';
import { ALERT_Z_INDEX, TOAST_Z_INDEX } from '@/styles';

export interface UseConstructionCaseItem {
  id: number;
  name: string;
  landingUrl: string;
  imageUrl: string;
  scrapTypeCode: ScrapType;
  scrapYn: YesOrNo;
}

const useConstructionCaseItem = ({
  id,
  name,
  landingUrl,
  imageUrl,
  scrapTypeCode,
  scrapYn,
}: UseConstructionCaseItem) => {
  const store = useStore();
  const { isAuthenticated } = useAuthenticated();
  const { createScraps, deleteScraps } = scrapGateway();
  const { loginGateway } = authGateway(store.dispatch);
  const { toast } = useToast();
  const { handleOpen: handleAlertOpen } = useAlert();
  const { handleOpen: handleConfirmOpen } = useConfirm();
  const router = useRouter();
  const [isScrap, setIsScrap] = useState(scrapYn);

  const hasScrap = useMemo(() => isScrap === 'Y', [isScrap]);

  const handleLogin = useCallback(async () => {
    await loginGateway.exec();
  }, [loginGateway]);

  const handleDeleteScraps = useCallback(async () => {
    const { code, message } = await deleteScraps.exec({
      scrapTypeCode,
      targetId: id,
    });

    if (code !== 200 && message) {
      handleAlertOpen({
        content: message,
        zIndex: ALERT_Z_INDEX,
      });
      return 'Y';
    }

    return 'N';
  }, [deleteScraps, handleAlertOpen, id, scrapTypeCode]);

  const handleCreateScraps = useCallback(async () => {
    const { code, message } = await createScraps.exec({
      scrapTypeCode,
      target: {
        id,
        name,
        landingUrl,
        imageUrl,
      },
    });

    if (code !== 200 && message) {
      handleAlertOpen({
        content: message,
        zIndex: ALERT_Z_INDEX,
      });
      return 'N';
    }

    return 'Y';
  }, [
    createScraps,
    handleAlertOpen,
    id,
    imageUrl,
    landingUrl,
    name,
    scrapTypeCode,
  ]);

  const handleGoScrap = useCallback(() => {
    router.push(
      `${process.env.NEXT_PUBLIC_HANSSEMMALL_HOST}/mypage/wishList.do`,
    );
  }, [router]);

  const handleScrap = useCallback(async () => {
    if (!isAuthenticated) {
      handleConfirmOpen({
        content: '로그인 후 이용 가능합니다. \n 로그인 하시겠습니까?',
        cancelText: '취소',
        confirmText: '확인',
        handleConfirm: () => {
          handleLogin();
        },
      });

      return;
    }

    console.log('로그인 검증 완료');

    if (hasScrap) {
      setIsScrap('N');
      const scrapResult = await handleDeleteScraps();
      setIsScrap(scrapResult);
    } else {
      setIsScrap('Y');
      const scrapResult = await handleCreateScraps();
      setIsScrap(scrapResult);
      if (scrapResult === 'Y') {
        toast.success('찜 목록에 추가하였습니다.', {
          placement: 'bottom',
          variant: 'button',
          actionButton: (
            <NewTextButton
              size="lg"
              isUnderline
              color="color_chip.white"
              onClick={handleGoScrap}
              width={74}
            >
              찜 목록가기
            </NewTextButton>
          ),
          offset: 87,
          zIndex: TOAST_Z_INDEX,
        });
      }
    }
  }, [
    isAuthenticated,
    hasScrap,
    handleConfirmOpen,
    handleLogin,
    handleDeleteScraps,
    handleCreateScraps,
    toast,
    handleGoScrap,
  ]);

  useEffect(() => {
    setIsScrap(scrapYn);
  }, [scrapYn]);

  return { hasScrap, handleScrap };
};

export default useConstructionCaseItem;
